import React, { useEffect, useState, useMemo } from "react";
import { useTypesDataContext } from "@/providers/TypesDataProvider";
import { useGlossaryDataContext } from "@/providers/ObjectsDataProvider";
import { useSimplifiedUserData } from '@/providers/SimplifiedUserDataProvider';
import { GlossarySectionFormProps } from "./SectionFormFunctions/objectFormTypes";
import { useGlossaryFormState } from "./SectionFormFunctions/objectFormState";
import { GlossaryFieldRenderer } from "./ObjectFormFieldRenderer";
import { hasSectionsStructureChanged } from "./SectionFormFunctions/objectFormUtils";
import { BlueprintVersionChanges, sectionHasChanges } from "@/lib/utils/blueprintVersionUtils";
import { getSelectedGame, getUserRoleInCompany, getCurrentUser } from "@/lib/stores/unifiedUserStore";
import { getCachedLibraryItems } from "@/lib/stores/objectsStore";
import { getCachedDialogs } from "@/lib/stores/dialogStore";
import IconProvider from "@/components/ui/IconProvider";
import { DocumentationTooltip } from "@/components/ui/DocumentationTooltip";
import { getCompanyRoles, getUserRole } from "@/lib/utils/permissions";
import { useDevRoleOverride } from "@/lib/utils/devRoleOverride";

import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';
/**
 * Main component for rendering glossary section forms
 */
interface ExtendedGlossarySectionFormProps extends GlossarySectionFormProps {
  canEdit?: boolean; // Whether user can edit (controls field locking)
  blueprintChanges?: BlueprintVersionChanges | null;
  showDocumentation?: boolean;
}

export const GlossarySectionForm: React.FC<ExtendedGlossarySectionFormProps> = ({
  sections,
  onInputChange,
  mode = "create",
  canEdit = true, // Default to true for backwards compatibility
  gameId,
  blueprintChanges,
  showDocumentation = false,
}) => {
  // Get types and glossary data for resolving IDs to names/descriptions
  const { types } = useTypesDataContext();
  const { selectedGame, currentUser } = useSimplifiedUserData();
  const currentGameId = gameId || selectedGame?.id;
  const { t } = usePlatformTranslation();
  
  // Get all library items (both glossary and document types) for proper ID resolution in view mode
  const glossaryItems = currentGameId ? getCachedLibraryItems(currentGameId) : [];
  
  // Get all dialogs for proper ID resolution in view mode
  const dialogs = currentGameId ? getCachedDialogs(currentGameId) : [];

  // Subscribe to dev role override to trigger re-renders when it changes
  const { overrideRoleId } = useDevRoleOverride();
  
  // Get the effective user's role in this company (includes dev override)
  const userRoleId = useMemo(() => {
    if (!selectedGame?.company_id) return null;
    
    // Use getUserRole which automatically checks for dev role override
    const roleName = getUserRole(selectedGame.company_id);
    if (!roleName) return null;
    
    // Convert role name to role ID
    const companyRoles = getCompanyRoles(selectedGame.company_id);
    const role = companyRoles.find(r => r.role_name.toLowerCase() === roleName.toLowerCase());
    
    console.log('[ObjectSectionForm] Effective role:', roleName, '→ ID:', role?.id, '(override:', overrideRoleId, ')');
    return role?.id || null;
  }, [selectedGame?.company_id, overrideRoleId]);

  // Filter sections and fields based on user's role
  const filteredSections = useMemo(() => {
    if (!userRoleId) {
      // No role found, show all sections (fallback to permissive)
      return sections;
    }

    return sections
      .filter(section => {
        // Hide section if user's role is in hiddenForRoles
        if (section.hiddenForRoles?.includes(userRoleId)) {
          console.log('[ObjectSectionForm] Hiding section:', section.title, 'for role:', userRoleId);
          return false;
        }
        return true;
      })
      .map(section => ({
        ...section,
        fields: section.fields
          .filter(field => {
            // Hide field if user's role is in hiddenForRoles
            if (field.hiddenForRoles?.includes(userRoleId)) {
              console.log('[ObjectSectionForm] Hiding field:', field.label, 'in section:', section.title);
              return false;
            }
            return true;
          })
          .map(field => ({
            ...field,
            // Mark field as readonly if user's role is in readOnlyForRoles
            isReadOnly: field.readOnlyForRoles?.includes(userRoleId) || 
                       section.readOnlyForRoles?.includes(userRoleId) || false,
          }))
      }));
  }, [sections, userRoleId]);

  // Use custom hook for form state management (with filtered sections)
  const {
    localFormData,
    setLocalFormData,
    prevSectionsRef,
    getStringContent,
    getCurrentFieldContent,
    getFieldValue,
    handleLocalInputChange,
    handleFieldBlur,
    handleMultipleFieldChange,
    addFieldInstance,
    removeFieldInstance,
    moveFieldInstance,
    reorderFieldInstances,
  } = useGlossaryFormState(filteredSections, onInputChange, currentGameId as string);

  // Track collapsed state for each section
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());

  const toggleSection = (sectionId: string) => {
    setCollapsedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      return newSet;
    });
  };

  // Update localFormData when filtered sections change
  useEffect(() => {
    // Only reset localFormData if the structure of sections changes
    // (number of sections, number of fields, or field IDs), not when values change
    if (hasSectionsStructureChanged(prevSectionsRef.current, filteredSections)) {
      setLocalFormData(filteredSections);
    }

    // Update the ref with current sections
    prevSectionsRef.current = filteredSections;
  }, [filteredSections, setLocalFormData, prevSectionsRef]);

  return (
    <div className="space-y-4">
      {filteredSections.map((section) => {
        const hasChanges = blueprintChanges && sectionHasChanges(section.id, blueprintChanges);
        const isNewSection = blueprintChanges?.addedSections.includes(section.id);
        const isCollapsed = collapsedSections.has(section.id);
        
        return (
          <div key={section.id} className="flex flex-col gap-6  ">
            <div 
              className="flex items-center justify-between cursor-pointer hover:opacity-80 transition-opacity border-b-2 border-border "
              onClick={() => toggleSection(section.id)}
            >
              <DocumentationTooltip documentation={section.documentation} disabled={mode === "view"}>
                <h3 className={`text-xl font-bold ${
                  hasChanges 
                    ? isNewSection
                      ? "text-green-600 dark:text-green-400" // New sections in green
                      : "text-red-600 dark:text-red-400"   // Modified sections in red
                    : "text-foreground"
                }`}>
                  {section.title}
                  {hasChanges && (
                    <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-opacity-20 font-normal">
                      {isNewSection ? (
                        <span className="bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-1.5 py-0.5 rounded">New</span>
                      ) : (
                        <span className="bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 px-1.5 py-0.5 rounded">{t('platform.wizards.glossary.modified')}</span>
                      )}
                    </span>
                  )}
                </h3>
              </DocumentationTooltip>
              <IconProvider 
                iconstring={isCollapsed ? "fa-chevron-down" : "fa-chevron-up"} 
                size={14} 
                className="text-muted-foreground"
              />
            </div>

            {/* Section Documentation Display - When documentation mode is on */}
          {/*   {showDocumentation && section.documentation && !isCollapsed && (
              <div className="mb-3 p-3 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded">
                <div 
                  className="text-sm text-foreground prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: section.documentation }}
                />
              </div>
            )} */}

            {/* Render fields in the correct order */}
            {!isCollapsed && (
            <div className="space-y-3">
              {section.fields.map((field) => (
                <GlossaryFieldRenderer
                  key={field.id}
                  field={field}
                  mode={mode}
                  canEdit={canEdit}
                  fieldValue={getFieldValue(field.id)}
                  onLocalInputChange={handleLocalInputChange}
                  onFieldBlur={handleFieldBlur}
                  onMultipleFieldChange={handleMultipleFieldChange}
                  onAddFieldInstance={addFieldInstance}
                  onRemoveFieldInstance={removeFieldInstance}
                  onMoveFieldInstance={moveFieldInstance}
                  onReorderFieldInstances={reorderFieldInstances}
                  getCurrentFieldContent={getCurrentFieldContent}
                  types={types}
                  glossaryItems={glossaryItems}
                  dialogs={dialogs}
                  getStringContent={getStringContent}
                  blueprintChanges={blueprintChanges}
                  gameId={gameId}
                  showDocumentation={showDocumentation}
                />
              ))}
            </div>
            )}
          </div>
        );
      })}
    </div>
  );
};